// MemberRegistrationPage.jsx
import React, { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import Icon from "@mui/material/Icon";
import MDButton from "components/MDButton";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import Swal from "sweetalert2";
import Grid from "@mui/material/Grid";
import CircularProgress from "@mui/material/CircularProgress";
import Avatar from "@mui/material/Avatar";
import Card from "@mui/material/Card";
import MenuItem from "@mui/material/MenuItem";
import InputLabel from "@mui/material/InputLabel";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import MDInput from "components/MDInput";
import { useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { API_BASE, API_HOST } from "config/api";

// Country codes for international members
const COUNTRY_CODES = {
  Ghana: "+233",
  Nigeria: "+234",
  "United States": "+1",
  "United Kingdom": "+44",
  Canada: "+1",
  "South Africa": "+27",
  Kenya: "+254",
  India: "+91",
  Germany: "+49",
  France: "+33",
  Italy: "+39",
  Spain: "+34",
  Australia: "+61",
  China: "+86",
  Japan: "+81",
  Brazil: "+55",
  "South Korea": "+82",
  Netherlands: "+31",
  Sweden: "+46",
  Norway: "+47",
  Denmark: "+45",
  Finland: "+358",
  Ireland: "+353",
  Switzerland: "+41",
  Austria: "+43",
  Belgium: "+32",
  Portugal: "+351",
  Greece: "+30",
  Turkey: "+90",
  "United Arab Emirates": "+971",
  Israel: "+972",
  "Saudi Arabia": "+966",
  Qatar: "+974",
  Kuwait: "+965",
  Egypt: "+20",
  Morocco: "+212",
  Tunisia: "+216",
  Algeria: "+213",
  Libya: "+218",
  Sudan: "+249",
  Ethiopia: "+251",
  Somalia: "+252",
  Tanzania: "+255",
  Uganda: "+256",
  Rwanda: "+250",
  Burundi: "+257",
  "DR Congo": "+243",
  Congo: "+242",
  Cameroon: "+237",
  "Central African Republic": "+236",
  Chad: "+235",
  Niger: "+227",
  Mali: "+223",
  "Burkina Faso": "+226",
  Benin: "+229",
  Togo: "+228",
  "Côte d'Ivoire": "+225",
  Liberia: "+231",
  "Sierra Leone": "+232",
  Guinea: "+224",
  "Guinea-Bissau": "+245",
  Senegal: "+221",
  Gambia: "+220",
  "Cabo Verde": "+238",
  Mauritania: "+222",
  Zambia: "+260",
  Zimbabwe: "+263",
  Malawi: "+265",
  Mozambique: "+258",
  Botswana: "+267",
  Namibia: "+264",
  Angola: "+244",
  "South Sudan": "+211",
  Eritrea: "+291",
  Djibouti: "+253",
  Comoros: "+269",
  Mauritius: "+230",
  Seychelles: "+248",
};

const getCountryCode = (country) => COUNTRY_CODES[country] || "+233";

const MemberRegistrationPage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { churchId } = useParams();

  const [currentStep, setCurrentStep] = useState(1);
  const [isSaving, setIsSaving] = useState(false);
  const [registeredMember, setRegisteredMember] = useState(null);
  const [viewMode, setViewMode] = useState("register");
  const [departments, setDepartments] = useState([]);
  const [ministries, setMinistries] = useState([]);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [signaturePreview, setSignaturePreview] = useState(null);
  const [church, setChurch] = useState(null);
  const [churchError, setChurchError] = useState(false);
  const churchName = church?.name || "your church";

  const [selectedMember, setSelectedMember] = useState({
    id: null,
    memberId: "",
    firstName: "",
    middleName: "",
    surname: "",
    gender: "",
    dateOfBirth: "",
    maritalStatus: "",
    country: "Ghana",
    phoneNumber: "",
    email: "",
    residentialAddress: "",
    region: "",
    homeTown: "",
    postalAddress: "",
    spouseName: "",
    spousePhoneNumber: "",
    dateOfMarriage: "",
    byPastor: "",
    numberOfChildren: 0,
    numberOfMales: 0,
    numberOfFemales: 0,
    nameOfFather: "",
    fatherLifeStatus: "",
    nameOfMother: "",
    motherLifeStatus: "",
    nextOfKin: "",
    nextOfKinRelationship: "",
    nextOfKinPhone: "",
    yearOfConversion: "",
    areYouBaptized: "",
    baptismType: "",
    baptismDate: "",
    baptismChurch: "",
    baptismBy: "",
    baptismCertificate: "",
    nameOfPreviousChurch: "",
    previousChurchAddress: "",
    placeOfInterest: "",
    departmentId: "",
    ministryId: "",
    willYouPayTithe: "",
    communicant: "",
    willYouPayWelfare: "",
    membershipDate: new Date().toISOString().split("T")[0],
    role: "member",
    status: "active",
    languagesSpoken: "",
    occupation: "",
    profession: "",
    rank: "",
    employment: "",
    nameOfEmployer: "",
    education: "",
    nationality: "",
    placeOfBirth: "",
    otherNumber: "",
    whatsappNumber: "",
    photoFile: null,
    signatureFile: null,
    photo: "",
    signature: "",
    phone_country_code: "+233",
    country_code: "+233",
  });

  const fetchChurch = useCallback(async () => {
    if (!churchId) return;
    try {
      const response = await fetch(`${API_BASE}/churches/list/${churchId}`);
      if (!response.ok) throw new Error("Church not found");
      const data = await response.json();
      const churchData = data?.id ? data : data?.data || data;
      if (!churchData?.id) throw new Error("Church not found");
      setChurch(churchData);
    } catch (error) {
      console.error("Failed to fetch church:", error);
      setChurchError(true);
    }
  }, [churchId]);

  const fetchDepartments = useCallback(async () => {
    if (!churchId) return;
    try {
      const response = await fetch(`${API_BASE}/departments/church/${churchId}`);
      if (response.ok) {
        const data = await response.json();
        setDepartments(data.data || data.departments || data);
      } else {
        setDepartments([]);
      }
    } catch (error) {
      console.error("Failed to fetch departments:", error);
      setDepartments([]);
    }
  }, [churchId]);

  const fetchMinistries = useCallback(async () => {
    if (!churchId) return;
    try {
      const response = await fetch(`${API_BASE}/ministries/church/${churchId}`);
      if (response.ok) {
        const data = await response.json();
        setMinistries(data.data || data.ministries || data);
      } else {
        setMinistries([]);
      }
    } catch (error) {
      console.error("Failed to fetch ministries:", error);
      setMinistries([]);
    }
  }, [churchId]);

  useEffect(() => {
    fetchChurch();
    fetchDepartments();
    fetchMinistries();
  }, [fetchChurch, fetchDepartments, fetchMinistries]);

  // Auto-assign ministry based on age + gender when either changes
  useEffect(() => {
    if (!ministries.length || !selectedMember.dateOfBirth) return;
    const dob = new Date(selectedMember.dateOfBirth);
    const today = new Date();
    let age = today.getFullYear() - dob.getFullYear();
    const m = today.getMonth() - dob.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < dob.getDate())) age--;

    let keyword;
    if (age < 18) keyword = "child";
    else if (age <= 25) keyword = "youth";
    else keyword = (selectedMember.gender || "").toLowerCase() === "female" ? "women" : "men";

    const match = ministries.find((mn) => mn.name.toLowerCase().includes(keyword));
    if (match && match.id !== selectedMember.ministryId) {
      setSelectedMember((prev) => ({ ...prev, ministryId: match.id }));
    }
  }, [selectedMember.dateOfBirth, selectedMember.gender, ministries]);

  const handlePhotoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const previewUrl = URL.createObjectURL(file);
      setPhotoPreview(previewUrl);
      setSelectedMember((prev) => ({ ...prev, photoFile: file, photoPreview: previewUrl }));
    }
  };

  const handleSignatureUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const previewUrl = URL.createObjectURL(file);
      setSignaturePreview(previewUrl);
      setSelectedMember((prev) => ({ ...prev, signatureFile: file, signaturePreview: previewUrl }));
    }
  };

  const validateStep1 = () => {
    if (!selectedMember.firstName?.trim()) return "First name is required";
    if (!selectedMember.surname?.trim()) return "Surname is required";
    if (!selectedMember.gender) return "Gender is required";
    if (!selectedMember.residentialAddress?.trim()) return "Residential address is required";
    if (!selectedMember.country) return "Country is required";
    if (!selectedMember.maritalStatus) return "Marital Status is required";
    // Non-Ghana members receive login credentials by email, so it's required.
    if (selectedMember.country !== "Ghana" && !selectedMember.email?.trim()) {
      return "Email is required for members outside Ghana (login details will be emailed)";
    }
    return null;
  };

  const validateStep2 = () => {
    if (!selectedMember.areYouBaptized) return "Are you Baptized field is required";
    return null;
  };

  const validateStep3 = () => {
    if (!selectedMember.willYouPayTithe) return "Will you pay Tithe is required";
    if (!selectedMember.willYouPayWelfare) return "Will you pay Welfare is required";
    if (!selectedMember.membershipDate) return "Membership Date is required";
    return null;
  };

  const showErrorToast = (message) => {
    Swal.fire({
      title: "Error!",
      text: message,
      icon: "error",
      position: "top",
      toast: true,
      showConfirmButton: false,
      timer: 3000,
    });
  };

  const handleNext = () => {
    let error = null;
    if (currentStep === 1) error = validateStep1();
    if (currentStep === 2) error = validateStep2();

    if (error) {
      showErrorToast(error);
      return;
    }

    if (currentStep < 3) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = async () => {
    const step1Error = validateStep1();
    if (step1Error) {
      showErrorToast(step1Error);
      setCurrentStep(1);
      return;
    }
    const step2Error = validateStep2();
    if (step2Error) {
      showErrorToast(step2Error);
      setCurrentStep(2);
      return;
    }
    const step3Error = validateStep3();
    if (step3Error) {
      showErrorToast(step3Error);
      setCurrentStep(3);
      return;
    }

    setIsSaving(true);
    const formData = new FormData();

    // Append all form fields (same as before)
    formData.append("first_name", selectedMember.firstName);
    formData.append("middle_name", selectedMember.middleName || "");
    formData.append("surname", selectedMember.surname);
    formData.append("gender", selectedMember.gender);
    formData.append("date_of_birth", selectedMember.dateOfBirth);
    formData.append("marital_status", selectedMember.maritalStatus);
    formData.append("phone", selectedMember.phoneNumber);
    if (
      selectedMember.phone_country_code &&
      !String(selectedMember.phoneNumber || "").startsWith("+")
    ) {
      formData.append("phone_country_code", selectedMember.phone_country_code);
    }
    formData.append("other_number", selectedMember.otherNumber || "");
    formData.append("whatsapp_number", selectedMember.whatsappNumber || "");
    formData.append("email", selectedMember.email || "");
    formData.append("residential_address", selectedMember.residentialAddress);
    formData.append("postal_address", selectedMember.postalAddress || "");
    formData.append("region", selectedMember.region || "");
    formData.append("home_town", selectedMember.homeTown || "");
    formData.append("country", selectedMember.country);
    formData.append("nationality", selectedMember.nationality || "");
    formData.append("place_of_birth", selectedMember.placeOfBirth || "");
    formData.append("occupation", selectedMember.occupation || "");
    formData.append("profession", selectedMember.profession || "");
    formData.append("rank", selectedMember.rank || "");
    formData.append("employment", selectedMember.employment || "");
    formData.append("name_of_employer", selectedMember.nameOfEmployer || "");
    formData.append("education", selectedMember.education || "");
    formData.append("spouse_name", selectedMember.spouseName || "");
    formData.append("spouse_phone", selectedMember.spousePhoneNumber || "");
    if (selectedMember.dateOfMarriage)
      formData.append("date_of_marriage", selectedMember.dateOfMarriage);
    formData.append("by_pastor", selectedMember.byPastor || "");
    formData.append("number_of_children", selectedMember.numberOfChildren || 0);
    formData.append("number_of_male", selectedMember.numberOfMales || 0);
    formData.append("number_of_female", selectedMember.numberOfFemales || 0);
    formData.append("name_of_father", selectedMember.nameOfFather || "");
    formData.append("father_life_status", selectedMember.fatherLifeStatus || "");
    formData.append("name_of_mother", selectedMember.nameOfMother || "");
    formData.append("mother_life_status", selectedMember.motherLifeStatus || "");
    formData.append("next_of_kin", selectedMember.nextOfKin || "");
    formData.append("next_of_kin_relationship", selectedMember.nextOfKinRelationship || "");
    formData.append("next_of_kin_phone", selectedMember.nextOfKinPhone || "");
    formData.append("year_of_conversion", selectedMember.yearOfConversion || "");
    if (selectedMember.areYouBaptized)
      formData.append("are_you_baptized", selectedMember.areYouBaptized);
    if (selectedMember.baptismType) formData.append("baptism_type", selectedMember.baptismType);
    if (selectedMember.baptismDate) formData.append("baptism_date", selectedMember.baptismDate);
    formData.append("baptism_church", selectedMember.baptismChurch || "");
    formData.append("baptism_by", selectedMember.baptismBy || "");
    formData.append("baptism_certificate", selectedMember.baptismCertificate || "");
    formData.append("name_of_previous_church", selectedMember.nameOfPreviousChurch);
    formData.append("previous_church_address", selectedMember.previousChurchAddress || "");
    formData.append("place_of_interest", selectedMember.placeOfInterest);
    formData.append("department_id", selectedMember.departmentId);
    if (selectedMember.ministryId) formData.append("ministry_id", selectedMember.ministryId);
    formData.append("will_you_pay_tithe", selectedMember.willYouPayTithe);
    formData.append("communicant", selectedMember.communicant || "");
    formData.append("will_you_pay_welfare", selectedMember.willYouPayWelfare);
    formData.append("membership_date", selectedMember.membershipDate);
    formData.append("role", selectedMember.role);
    formData.append("status", selectedMember.status === "active" ? "1" : "0");
    formData.append("languages", selectedMember.languagesSpoken || "");
    formData.append("church_id", churchId);

    if (selectedMember.photoFile) formData.append("photo", selectedMember.photoFile);
    if (selectedMember.signatureFile) formData.append("signature", selectedMember.signatureFile);

    try {
      // Use the new public registration endpoint
      const response = await fetch(`${API_BASE}/public/members/register`, {
        method: "POST",
        headers: {
          Accept: "application/json",
          // No Authorization header needed for public endpoint
        },
        body: formData,
      });

      const data = await response.json();

      if (!response.ok) {
        // Laravel validation errors come as { message, errors: { field: [msg] } }
        const firstErrors = data.errors
          ? Object.values(data.errors).flat().slice(0, 3).join("\n")
          : null;
        const detail = data.error ? `${data.message}: ${data.error}` : data.message;
        throw new Error(firstErrors || detail || "Registration failed");
      }

      // Store registered member info
      const newMember = data.data || data.member;
      const login = data.login || {}; // { channel: 'sms'|'email', username, delivered }
      const registeredInfo = {
        ...selectedMember,
        id: newMember.id,
        memberId: data.memberID || newMember.memberID || newMember.memberId,
        loginUsername: login.username || null,
        credentialsChannel: login.channel || null,
        credentialsSent: login.delivered === true,
      };

      const channelLabel = login.channel === "sms" ? "phone (SMS)" : "email";
      if (login.delivered) {
        Swal.fire({
          title: "Registration Successful! 🎉",
          html:
            `Welcome to ${churchName}!<br/><br/>` +
            `Your login has been created and details were sent to your ` +
            `<strong>${channelLabel}</strong>.`,
          icon: "success",
          confirmButtonColor: "#1976d2",
        });
      } else {
        Swal.fire({
          title: "Registration Successful! 🎉",
          text: `Welcome to ${churchName}! Your membership has been recorded.`,
          icon: "success",
          confirmButtonColor: "#1976d2",
        });
      }

      setRegisteredMember(registeredInfo);
      localStorage.setItem("registered_member", JSON.stringify(registeredInfo));
      setViewMode("view");
    } catch (error) {
      console.error("Registration error:", error);
      Swal.fire({
        title: "Registration Failed",
        text: error.message || "Something went wrong. Please try again.",
        icon: "error",
        confirmButtonColor: "#1976d2",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const resetForm = () => {
    Swal.fire({
      title: "Reset Form?",
      text: "All entered data will be cleared.",
      icon: "question",
      showCancelButton: true,
    }).then((result) => {
      if (result.isConfirmed) {
        setSelectedMember({
          ...selectedMember,
          firstName: "",
          middleName: "",
          surname: "",
          gender: "",
          dateOfBirth: "",
          maritalStatus: "",
          country: "Ghana",
          phoneNumber: "",
          email: "",
          residentialAddress: "",
          region: "",
          homeTown: "",
          postalAddress: "",
          spouseName: "",
          spousePhoneNumber: "",
          dateOfMarriage: "",
          byPastor: "",
          numberOfChildren: 0,
          numberOfMales: 0,
          numberOfFemales: 0,
          nameOfFather: "",
          fatherLifeStatus: "",
          nameOfMother: "",
          motherLifeStatus: "",
          nextOfKin: "",
          nextOfKinRelationship: "",
          nextOfKinPhone: "",
          yearOfConversion: "",
          areYouBaptized: "",
          baptismType: "",
          baptismDate: "",
          baptismChurch: "",
          baptismBy: "",
          nameOfPreviousChurch: "",
          placeOfInterest: "",
          departmentId: "",
          ministryId: "",
          willYouPayTithe: "",
          communicant: "",
          willYouPayWelfare: "",
          languagesSpoken: "",
          occupation: "",
          profession: "",
          rank: "",
          employment: "",
          nameOfEmployer: "",
          education: "",
          nationality: "",
          placeOfBirth: "",
          photoFile: null,
          signatureFile: null,
        });
        setPhotoPreview(null);
        setSignaturePreview(null);
        setCurrentStep(1);
      }
    });
  };

  // Invalid or unrecognized church link
  if (!churchId || churchError) {
    return (
      <MDBox
        py={3}
        px={isMobile ? 2 : 4}
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="60vh"
      >
        <Card sx={{ borderRadius: 4, p: 4, maxWidth: 420, textAlign: "center" }}>
          <Icon sx={{ fontSize: 56, color: "error.main", mb: 1 }}>error_outline</Icon>
          <MDTypography variant="h5" mb={1}>
            Registration link not found
          </MDTypography>
          <MDTypography variant="body2" color="text">
            This registration link is invalid or the church could not be found. Please ask the
            church office for the correct QR code or link.
          </MDTypography>
        </Card>
      </MDBox>
    );
  }

  // Church data still loading
  if (!church) {
    return (
      <MDBox
        py={3}
        px={isMobile ? 2 : 4}
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="60vh"
      >
        <CircularProgress />
      </MDBox>
    );
  }

  // Render View Mode
  if (viewMode === "view" && registeredMember) {
    return (
      <MDBox py={3} px={isMobile ? 2 : 4}>
        <Grid container justifyContent="center">
          <Grid item xs={12} md={8} lg={6}>
            <Card sx={{ borderRadius: 4, overflow: "hidden" }}>
              <MDBox variant="gradient" bgColor="info" p={3}>
                <MDBox
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  flexDirection="column"
                >
                  <Icon sx={{ fontSize: 60, color: "white", mb: 2 }}>verified_user</Icon>
                  <MDTypography variant="h4" color="white" fontWeight="bold" textAlign="center">
                    Welcome, {registeredMember.firstName}!
                  </MDTypography>
                  <MDTypography
                    variant="body2"
                    color="white"
                    opacity={0.9}
                    textAlign="center"
                    mt={1}
                  >
                    {churchName}
                  </MDTypography>
                </MDBox>
              </MDBox>

              <MDBox p={3}>
                <MDTypography variant="h6" mb={2}>
                  <Icon sx={{ mr: 1, verticalAlign: "middle" }}>badge</Icon>
                  Your Information
                </MDTypography>

                <MDBox sx={{ mb: 2 }}>
                  {registeredMember.memberId && (
                    <MDBox display="flex" mb={1.5}>
                      <MDTypography variant="body2" fontWeight="bold" width={130}>
                        Member ID:
                      </MDTypography>
                      <MDTypography variant="body2">{registeredMember.memberId}</MDTypography>
                    </MDBox>
                  )}
                  <MDBox display="flex" mb={1.5}>
                    <MDTypography variant="body2" fontWeight="bold" width={130}>
                      Full Name:
                    </MDTypography>
                    <MDTypography variant="body2">
                      {registeredMember.firstName} {registeredMember.middleName}{" "}
                      {registeredMember.surname}
                    </MDTypography>
                  </MDBox>
                  <MDBox display="flex" mb={1.5}>
                    <MDTypography variant="body2" fontWeight="bold" width={130}>
                      Phone:
                    </MDTypography>
                    <MDTypography variant="body2">{registeredMember.phoneNumber}</MDTypography>
                  </MDBox>
                  <MDBox display="flex" mb={1.5}>
                    <MDTypography variant="body2" fontWeight="bold" width={130}>
                      Email:
                    </MDTypography>
                    <MDTypography variant="body2">
                      {registeredMember.email || "Not provided"}
                    </MDTypography>
                  </MDBox>
                  <MDBox display="flex" mb={1.5}>
                    <MDTypography variant="body2" fontWeight="bold" width={130}>
                      Department:
                    </MDTypography>
                    <MDTypography variant="body2">
                      {departments.find((d) => d.id === registeredMember.departmentId)?.name ||
                        "Pending"}
                    </MDTypography>
                  </MDBox>
                  <MDBox display="flex" mb={1.5}>
                    <MDTypography variant="body2" fontWeight="bold" width={130}>
                      Membership Date:
                    </MDTypography>
                    <MDTypography variant="body2">{registeredMember.membershipDate}</MDTypography>
                  </MDBox>
                  {registeredMember.loginUsername && (
                    <MDBox display="flex" mb={1.5}>
                      <MDTypography variant="body2" fontWeight="bold" width={130}>
                        Login Sent To:
                      </MDTypography>
                      <MDTypography variant="body2">
                        {registeredMember.credentialsChannel === "sms"
                          ? `SMS — ${registeredMember.loginUsername}`
                          : `Email — ${registeredMember.loginUsername}`}
                        {registeredMember.credentialsSent ? "" : " (delivery failed)"}
                      </MDTypography>
                    </MDBox>
                  )}
                </MDBox>

                <MDBox display="flex" gap={2} justifyContent="flex-end" mt={3}>
                  <MDButton variant="gradient" color="info" onClick={() => setViewMode("register")}>
                    <Icon>edit</Icon>&nbsp;Edit Info
                  </MDButton>
                </MDBox>
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
    );
  }

  // Render Registration Form
  return (
    <MDBox py={3} px={isMobile ? 2 : 4}>
      <Grid container justifyContent="center">
        <Grid item xs={12} md={10} lg={8}>
          <Card sx={{ borderRadius: 4, overflow: "hidden" }}>
            <MDBox variant="gradient" bgColor="info" p={3}>
              <MDBox
                display="flex"
                alignItems="center"
                justifyContent="center"
                flexDirection="column"
              >
                <Icon sx={{ fontSize: 48, color: "white", mb: 1 }}>person_add</Icon>
                <MDTypography variant="h4" color="white" fontWeight="bold">
                  {churchName}
                </MDTypography>
                <MDTypography variant="body2" color="white" opacity={0.9} mt={1}>
                  Member Registration - Step {currentStep} of 3
                </MDTypography>
              </MDBox>
            </MDBox>

            <MDBox p={3}>
              {/* Step 1: Basic Information */}
              {currentStep === 1 && (
                <>
                  <MDTypography variant="h6" mb={2}>
                    <Icon sx={{ mr: 1, verticalAlign: "middle" }}>info</Icon>
                    Basic Information
                  </MDTypography>

                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <MDInput
                        label="First Name *"
                        value={selectedMember.firstName}
                        onChange={(e) =>
                          setSelectedMember({ ...selectedMember, firstName: e.target.value })
                        }
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <MDInput
                        label="Middle Name"
                        value={selectedMember.middleName}
                        onChange={(e) =>
                          setSelectedMember({ ...selectedMember, middleName: e.target.value })
                        }
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <MDInput
                        label="Surname *"
                        value={selectedMember.surname}
                        onChange={(e) =>
                          setSelectedMember({ ...selectedMember, surname: e.target.value })
                        }
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth>
                        <InputLabel>Gender *</InputLabel>
                        <Select
                          value={selectedMember.gender}
                          label="Gender *"
                          onChange={(e) =>
                            setSelectedMember({ ...selectedMember, gender: e.target.value })
                          }
                        >
                          <MenuItem value="Male">Male</MenuItem>
                          <MenuItem value="Female">Female</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <MDInput
                        label="Date of Birth"
                        type="date"
                        InputLabelProps={{ shrink: true }}
                        value={selectedMember.dateOfBirth}
                        onChange={(e) =>
                          setSelectedMember({ ...selectedMember, dateOfBirth: e.target.value })
                        }
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <MDInput
                        label="Place of Birth"
                        value={selectedMember.placeOfBirth}
                        onChange={(e) =>
                          setSelectedMember({ ...selectedMember, placeOfBirth: e.target.value })
                        }
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth>
                        <InputLabel>Marital Status *</InputLabel>
                        <Select
                          value={selectedMember.maritalStatus}
                          label="Marital Status *"
                          onChange={(e) =>
                            setSelectedMember({ ...selectedMember, maritalStatus: e.target.value })
                          }
                        >
                          <MenuItem value="Single">Single</MenuItem>
                          <MenuItem value="Married">Married</MenuItem>
                          <MenuItem value="Divorced">Divorced</MenuItem>
                          <MenuItem value="Widowed">Widowed</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <MDInput
                        label="Nationality"
                        value={selectedMember.nationality}
                        onChange={(e) =>
                          setSelectedMember({ ...selectedMember, nationality: e.target.value })
                        }
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth>
                        <InputLabel>Country *</InputLabel>
                        <Select
                          value={selectedMember.country}
                          label="Country *"
                          onChange={(e) => {
                            const country = e.target.value;
                            setSelectedMember({
                              ...selectedMember,
                              country,
                              phone_country_code: getCountryCode(country),
                            });
                          }}
                        >
                          {Object.keys(COUNTRY_CODES).map((country) => (
                            <MenuItem key={country} value={country}>
                              {country}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                      <MDInput
                        label="Residential Address *"
                        value={selectedMember.residentialAddress}
                        onChange={(e) =>
                          setSelectedMember({
                            ...selectedMember,
                            residentialAddress: e.target.value,
                          })
                        }
                        fullWidth
                        multiline
                        rows={2}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <MDInput
                        label="Region/State"
                        value={selectedMember.region}
                        onChange={(e) =>
                          setSelectedMember({ ...selectedMember, region: e.target.value })
                        }
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <MDInput
                        label="Postal Address"
                        value={selectedMember.postalAddress}
                        onChange={(e) =>
                          setSelectedMember({ ...selectedMember, postalAddress: e.target.value })
                        }
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <MDInput
                        label="Phone Number"
                        value={selectedMember.phoneNumber}
                        onChange={(e) =>
                          setSelectedMember({ ...selectedMember, phoneNumber: e.target.value })
                        }
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <MDInput
                        label="WhatsApp Number"
                        value={selectedMember.whatsappNumber}
                        onChange={(e) =>
                          setSelectedMember({ ...selectedMember, whatsappNumber: e.target.value })
                        }
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <MDInput
                        label="Email"
                        type="email"
                        value={selectedMember.email}
                        onChange={(e) =>
                          setSelectedMember({ ...selectedMember, email: e.target.value })
                        }
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <MDInput
                        label="Occupation"
                        value={selectedMember.occupation}
                        onChange={(e) =>
                          setSelectedMember({ ...selectedMember, occupation: e.target.value })
                        }
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <MDInput
                        label="Education Level"
                        value={selectedMember.education}
                        onChange={(e) =>
                          setSelectedMember({ ...selectedMember, education: e.target.value })
                        }
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <MDInput
                        label="Languages Spoken"
                        value={selectedMember.languagesSpoken}
                        onChange={(e) =>
                          setSelectedMember({ ...selectedMember, languagesSpoken: e.target.value })
                        }
                        fullWidth
                        placeholder="e.g., English, Twi, French"
                      />
                    </Grid>
                  </Grid>
                </>
              )}

              {/* Step 2: Family & Spiritual Information */}
              {currentStep === 2 && (
                <>
                  <MDTypography variant="h6" mb={2}>
                    <Icon sx={{ mr: 1, verticalAlign: "middle" }}>family_restroom</Icon>
                    Family & Spiritual Information
                  </MDTypography>

                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <MDInput
                        label="Spouse Name"
                        value={selectedMember.spouseName}
                        onChange={(e) =>
                          setSelectedMember({ ...selectedMember, spouseName: e.target.value })
                        }
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <MDInput
                        label="Spouse Phone"
                        value={selectedMember.spousePhoneNumber}
                        onChange={(e) =>
                          setSelectedMember({
                            ...selectedMember,
                            spousePhoneNumber: e.target.value,
                          })
                        }
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <MDInput
                        label="Date of Marriage"
                        type="date"
                        InputLabelProps={{ shrink: true }}
                        value={selectedMember.dateOfMarriage}
                        onChange={(e) =>
                          setSelectedMember({ ...selectedMember, dateOfMarriage: e.target.value })
                        }
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <MDInput
                        label="Married By"
                        value={selectedMember.byPastor}
                        onChange={(e) =>
                          setSelectedMember({ ...selectedMember, byPastor: e.target.value })
                        }
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <MDInput
                        label="Number of Children"
                        type="number"
                        value={selectedMember.numberOfChildren}
                        onChange={(e) =>
                          setSelectedMember({
                            ...selectedMember,
                            numberOfChildren: parseInt(e.target.value) || 0,
                          })
                        }
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <MDInput
                        label="Sons"
                        type="number"
                        value={selectedMember.numberOfMales}
                        onChange={(e) =>
                          setSelectedMember({
                            ...selectedMember,
                            numberOfMales: parseInt(e.target.value) || 0,
                          })
                        }
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <MDInput
                        label="Daughters"
                        type="number"
                        value={selectedMember.numberOfFemales}
                        onChange={(e) =>
                          setSelectedMember({
                            ...selectedMember,
                            numberOfFemales: parseInt(e.target.value) || 0,
                          })
                        }
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <MDInput
                        label="Father's Name"
                        value={selectedMember.nameOfFather}
                        onChange={(e) =>
                          setSelectedMember({ ...selectedMember, nameOfFather: e.target.value })
                        }
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <MDInput
                        label="Father's Status"
                        value={selectedMember.fatherLifeStatus}
                        onChange={(e) =>
                          setSelectedMember({ ...selectedMember, fatherLifeStatus: e.target.value })
                        }
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <MDInput
                        label="Mother's Name"
                        value={selectedMember.nameOfMother}
                        onChange={(e) =>
                          setSelectedMember({ ...selectedMember, nameOfMother: e.target.value })
                        }
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <MDInput
                        label="Mother's Status"
                        value={selectedMember.motherLifeStatus}
                        onChange={(e) =>
                          setSelectedMember({ ...selectedMember, motherLifeStatus: e.target.value })
                        }
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <MDInput
                        label="Next of Kin"
                        value={selectedMember.nextOfKin}
                        onChange={(e) =>
                          setSelectedMember({ ...selectedMember, nextOfKin: e.target.value })
                        }
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <MDInput
                        label="Next of Kin Phone"
                        value={selectedMember.nextOfKinPhone}
                        onChange={(e) =>
                          setSelectedMember({ ...selectedMember, nextOfKinPhone: e.target.value })
                        }
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <MDInput
                        label="Next of Kin Relationship"
                        value={selectedMember.nextOfKinRelationship}
                        onChange={(e) =>
                          setSelectedMember({
                            ...selectedMember,
                            nextOfKinRelationship: e.target.value,
                          })
                        }
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <MDInput
                        label="Year of Conversion"
                        type="number"
                        value={selectedMember.yearOfConversion}
                        onChange={(e) =>
                          setSelectedMember({ ...selectedMember, yearOfConversion: e.target.value })
                        }
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth>
                        <InputLabel>Are you Baptized? *</InputLabel>
                        <Select
                          value={selectedMember.areYouBaptized}
                          label="Are you Baptized? *"
                          onChange={(e) =>
                            setSelectedMember({ ...selectedMember, areYouBaptized: e.target.value })
                          }
                        >
                          <MenuItem value="Yes">Yes</MenuItem>
                          <MenuItem value="No">No</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth>
                        <InputLabel>Baptism Type</InputLabel>
                        <Select
                          value={selectedMember.baptismType}
                          label="Baptism Type"
                          onChange={(e) =>
                            setSelectedMember({ ...selectedMember, baptismType: e.target.value })
                          }
                        >
                          <MenuItem value="Immersion">Immersion</MenuItem>
                          <MenuItem value="Sprinkling">Sprinkling</MenuItem>
                          <MenuItem value="Pouring">Pouring</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <MDInput
                        label="Baptism Date"
                        type="date"
                        InputLabelProps={{ shrink: true }}
                        value={selectedMember.baptismDate}
                        onChange={(e) =>
                          setSelectedMember({ ...selectedMember, baptismDate: e.target.value })
                        }
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <MDInput
                        label="Baptism Church"
                        value={selectedMember.baptismChurch}
                        onChange={(e) =>
                          setSelectedMember({ ...selectedMember, baptismChurch: e.target.value })
                        }
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <MDInput
                        label="Baptized By"
                        value={selectedMember.baptismBy}
                        onChange={(e) =>
                          setSelectedMember({ ...selectedMember, baptismBy: e.target.value })
                        }
                        fullWidth
                      />
                    </Grid>
                  </Grid>
                </>
              )}

              {/* Step 3: Church Life */}
              {currentStep === 3 && (
                <>
                  <MDTypography variant="h6" mb={2}>
                    <Icon sx={{ mr: 1, verticalAlign: "middle" }}>church</Icon>
                    Church Life
                  </MDTypography>

                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <MDInput
                        label="Name of Previous Church"
                        value={selectedMember.nameOfPreviousChurch}
                        onChange={(e) =>
                          setSelectedMember({
                            ...selectedMember,
                            nameOfPreviousChurch: e.target.value,
                          })
                        }
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <MDInput
                        label="Previous Church Address"
                        value={selectedMember.previousChurchAddress}
                        onChange={(e) =>
                          setSelectedMember({
                            ...selectedMember,
                            previousChurchAddress: e.target.value,
                          })
                        }
                        fullWidth
                        multiline
                        rows={2}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <FormControl fullWidth>
                        <InputLabel id="poi-label">Place of Interest / Ministry</InputLabel>
                        <Select
                          labelId="poi-label"
                          value={selectedMember.placeOfInterest || ""}
                          label="Place of Interest / Ministry"
                          onChange={(e) =>
                            setSelectedMember({
                              ...selectedMember,
                              placeOfInterest: e.target.value,
                            })
                          }
                        >
                          <MenuItem value="">None</MenuItem>
                          <MenuItem value="Music Ministry">Music Ministry</MenuItem>
                          <MenuItem value="Ushering">Ushering</MenuItem>
                          <MenuItem value="Prayer Tower">Prayer Tower</MenuItem>
                          <MenuItem value="Evangelism">Evangelism</MenuItem>
                          <MenuItem value="Teaching">Teaching</MenuItem>
                          <MenuItem value="Protocol">Protocol</MenuItem>
                          <MenuItem value="Outside Cleaners">Outside Cleaners</MenuItem>
                          <MenuItem value="F.O.C">F.O.C</MenuItem>
                          <MenuItem value="Janitors">Janitors</MenuItem>
                          <MenuItem value="Media">Media</MenuItem>
                          <MenuItem value="Other">Other</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth>
                        <InputLabel>Department *</InputLabel>
                        <Select
                          value={selectedMember.departmentId}
                          label="Department *"
                          onChange={(e) =>
                            setSelectedMember({ ...selectedMember, departmentId: e.target.value })
                          }
                        >
                          <MenuItem value="">Select Department</MenuItem>
                          {departments.map((dept) => (
                            <MenuItem key={dept.id} value={dept.id}>
                              {dept.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth>
                        <InputLabel>Ministry (Optional)</InputLabel>
                        <Select
                          value={selectedMember.ministryId}
                          label="Ministry (Optional)"
                          onChange={(e) =>
                            setSelectedMember({ ...selectedMember, ministryId: e.target.value })
                          }
                        >
                          <MenuItem value="">Select Ministry</MenuItem>
                          {ministries.map((min) => (
                            <MenuItem key={min.id} value={min.id}>
                              {min.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth>
                        <InputLabel>Will you pay Tithe? *</InputLabel>
                        <Select
                          value={selectedMember.willYouPayTithe}
                          label="Will you pay Tithe? *"
                          onChange={(e) =>
                            setSelectedMember({
                              ...selectedMember,
                              willYouPayTithe: e.target.value,
                            })
                          }
                        >
                          <MenuItem value="Yes">Yes</MenuItem>
                          <MenuItem value="No">No</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth>
                        <InputLabel>Will you pay Welfare? *</InputLabel>
                        <Select
                          value={selectedMember.willYouPayWelfare}
                          label="Will you pay Welfare? *"
                          onChange={(e) =>
                            setSelectedMember({
                              ...selectedMember,
                              willYouPayWelfare: e.target.value,
                            })
                          }
                        >
                          <MenuItem value="Yes">Yes</MenuItem>
                          <MenuItem value="No">No</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth>
                        <InputLabel>Communicant</InputLabel>
                        <Select
                          value={selectedMember.communicant}
                          label="Communicant"
                          onChange={(e) =>
                            setSelectedMember({ ...selectedMember, communicant: e.target.value })
                          }
                        >
                          <MenuItem value="Yes">Yes</MenuItem>
                          <MenuItem value="No">No</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <MDInput
                        label="Membership Date *"
                        type="date"
                        InputLabelProps={{ shrink: true }}
                        value={selectedMember.membershipDate}
                        onChange={(e) =>
                          setSelectedMember({ ...selectedMember, membershipDate: e.target.value })
                        }
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <MDTypography variant="button" fontWeight="bold" mb={1}>
                        Upload Photo
                      </MDTypography>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handlePhotoUpload}
                        style={{ width: "100%", marginTop: "8px" }}
                      />
                      {photoPreview && (
                        <MDBox mt={1}>
                          <Avatar src={photoPreview} sx={{ width: 60, height: 60 }} />
                        </MDBox>
                      )}
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <MDTypography variant="button" fontWeight="bold" mb={1}>
                        Upload Signature
                      </MDTypography>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleSignatureUpload}
                        style={{ width: "100%", marginTop: "8px" }}
                      />
                    </Grid>
                  </Grid>
                </>
              )}

              {/* Navigation Buttons */}
              <MDBox display="flex" justifyContent="space-between" mt={4}>
                <MDBox>
                  {currentStep > 1 && (
                    <MDButton variant="gradient" color="secondary" onClick={handleBack}>
                      <Icon>arrow_back</Icon>&nbsp;Back
                    </MDButton>
                  )}
                </MDBox>
                <MDBox display="flex" gap={2}>
                  <MDButton variant="outlined" color="secondary" onClick={resetForm}>
                    Reset
                  </MDButton>
                  {currentStep < 3 ? (
                    <MDButton variant="gradient" color="info" onClick={handleNext}>
                      Next&nbsp;<Icon>arrow_forward</Icon>
                    </MDButton>
                  ) : (
                    <MDButton
                      variant="gradient"
                      color="success"
                      onClick={handleSubmit}
                      disabled={isSaving}
                    >
                      {isSaving ? (
                        <CircularProgress size={20} />
                      ) : (
                        <>
                          <Icon>check_circle</Icon>&nbsp;Register
                        </>
                      )}
                    </MDButton>
                  )}
                </MDBox>
              </MDBox>

              <MDBox textAlign="center" mt={3}>
                <MDTypography variant="caption" color="text">
                  Step {currentStep} of 3
                </MDTypography>
              </MDBox>
            </MDBox>
          </Card>
        </Grid>
      </Grid>
    </MDBox>
  );
};

MemberRegistrationPage.propTypes = {};

export default MemberRegistrationPage;
